import React, { useRef, Fragment } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Table,
} from "reactstrap";
import { AvForm, AvField } from "availity-reactstrap-validation";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toggleRightSidebar } from "../../store/actions";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useQuery, useMutation, useLazyQuery } from "react-apollo";
import { GET_WORKFLOWS } from "../../graphql/query/GET_WORKFLOWS";
import { GET_WORKFLOW } from "../../graphql/query/GET_WORKFLOW";
import { GET_USER } from "../../graphql/query/GET_USER";
import { GET_PAYMENT_TYPES } from "../../graphql/query/GET_PAYMENT_TYPES";
import { GET_BANK_ACCOUNTS } from "../../graphql/query/GET_BANK_ACCOUNTS";
import _ from "lodash";
import { Link, useHistory } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import moment from "moment";
import ToastHelper from "../../helpers/ToastHelper";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import NumberFormat from "react-number-format";
import DropZone from "../Workbench/Components/drop-zone";
import Compressor from "compressorjs";
import CloudinaryHelper from "../../helpers/CloudinaryHelper";
import TransactionItem from "../Transactions/transaction-item";
import { CREATE_ONE_WITHDRAWAL, FIND_OR_CREATE_MAIN_CREDIT_WALLET, LINK_TRANSACTION_TO_WORKFLOW } from "../../graphql/mutation";

const StatusBadge = ({ workflow }) => {
  if (workflow.completedAt) {
    return <span className="badge badge-success px-3 py-2 font-size-13">Completed</span>;
  } else if (workflow.errorMessage) {
    return <span className="badge badge-danger px-3 py-2 font-size-13">Error</span>;
  } else if (workflow.isActive) {
    return <span className="badge badge-warning px-3 py-2 font-size-13">In Progress</span>;
  } else {
    return <span className="badge badge-secondary px-3 py-2 font-size-13">Inactive</span>;
  }
};

const WorkflowTypeBadge = ({ workflowType }) => {
  // Format workflowType from camelCase to readable format
  const formatType = (type) => {
    if (!type) return '';
    return type
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };
  
  return (
    <span className="badge badge-primary px-2 py-1 font-size-11 ml-2" style={{ fontWeight: 'normal' }}>
      {formatType(workflowType)}
    </span>
  );
};

const WorkflowItem = ({ workflow: initialWorkflow, onSupersede }) => {
  const history = useHistory();
  const [countdown, setCountdown] = React.useState(15);
  const [currentWorkflow, setCurrentWorkflow] = React.useState(initialWorkflow);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isFinished = !!(currentWorkflow.completedAt || currentWorkflow.errorMessage);

  const [fetchWorkflow, { loading: refreshing }] = useLazyQuery(GET_WORKFLOW, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.workflow) {
        setCurrentWorkflow(data.workflow);
      }
    },
  });

  const handleRefresh = React.useCallback(() => {
    fetchWorkflow({ variables: { id: currentWorkflow.id } });
  }, [fetchWorkflow, currentWorkflow.id]);

  React.useEffect(() => {
    let interval = null;
    if (!isFinished) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            handleRefresh();
            return 15;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setCountdown(15);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isFinished, handleRefresh]);

  const workflow = currentWorkflow;

  return (
    <Col lg="12">
      <Card className="mb-3" style={{ borderLeft: `4px solid ${workflow.completedAt ? '#34c38f' : workflow.errorMessage ? '#f46a6a' : workflow.isActive ? '#f1b44c' : '#74788d'}` }}>
        <CardBody>
          {/* Header with Serial No and Status */}
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="d-flex align-items-start">
              <div className="mr-3">
                <h6 className="mb-1 font-weight-bold d-flex align-items-center">
                  {workflow.serialNo}
                  <WorkflowTypeBadge workflowType={workflow.workflowType} />
                </h6>
                <small className="text-muted">
                  {moment(workflow.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                </small>
              </div>
              {!isFinished && (
                <div className="d-flex align-items-center">
                  <span className="badge badge-soft-info font-size-11 px-2 py-1 mr-2" style={{ borderRadius: "5px" }}>
                    <i className="bx bx-sync bx-spin mr-1"></i>
                    {countdown}s
                  </span>
                  <i 
                    className={`bx bx-refresh font-size-18 text-primary cursor-pointer ${refreshing ? 'bx-spin' : ''}`}
                    onClick={() => {
                      handleRefresh();
                      setCountdown(15);
                    }}
                    title="Refresh Now"
                  ></i>
                </div>
              )}
            </div>
            <div className="d-flex align-items-center flex-wrap">
              {!workflow.completedAt && (
                <span className="badge-info px-3 py-2 font-size-12 mr-2" style={{ borderRadius: "5px" }}>
                  Step {workflow.currentStep}
                </span>
              )}
              <StatusBadge workflow={workflow} />
            </div>
          </div>

          {/* Player Info */}
          <div className="mb-3 p-2 bg-light rounded">
            <div className="d-flex align-items-center">
              <i className="bx bx-user-circle font-size-26 mr-2 text-primary"></i>
              <div>
                <div className="font-weight-medium d-flex align-items-center">
                  {workflow.player?.username}
                  <div 
                    className="cursor-pointer ml-2" 
                    onClick={() => {
                      history.push({
                        pathname: "/cashier-workbench",
                        target: "_blank",
                        state: {
                          user: {
                            node: workflow.player,
                          },
                        },
                      });
                    }}
                    title="Open in Workbench"
                  >
                    <i className="bx bxs-wrench font-size-18 text-primary"></i>
                  </div>
                </div>
                <small className="text-muted">{workflow.player?.name}</small>
              </div>
            </div>
          </div>

          {/* Info Grid - Amount, Payment Type, Company Bank, Current Step */}
          {!workflow.completedAt && workflow.currentStepTransaction && (
            <div className="mb-3 p-2 bg-soft-info rounded">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="bx bx-loader-circle font-size-20 mr-2 text-info"></i>
                  <div>
                    <div className="font-size-12 text-muted">Current Step Transaction</div>
                    <div className="font-weight-medium">
                      {workflow.currentStepTransaction.transactionType}
                    </div>
                  </div>
                </div>
                <span className={`badge px-2 py-1 font-size-12 ${
                  workflow.currentStepTransaction.transactionStatus === 'Completed' || workflow.currentStepTransaction.kioskStatus === 'Completed' 
                    ? 'badge-soft-success' 
                    : workflow.currentStepTransaction.transactionStatus === 'Rejected' || workflow.currentStepTransaction.kioskStatus === 'Failed'
                      ? 'badge-soft-danger'
                      : 'badge-soft-warning'
                }`}>
                  {workflow.currentStepTransaction.kioskStatus || workflow.currentStepTransaction.transactionStatus}
                </span>
              </div>
            </div>
          )}
          <div className="row mb-3">
            <div className="col-6">
              <div className="d-flex">
                <span className="text-muted mr-2">Quantity:</span>
                <span className="font-weight-bold text-danger">${parseFloat(workflow.quantity || 0).toFixed(2)}</span>
              </div>
            </div>
            <div className="col-6">
              <div className="d-flex">
                <span className="text-muted mr-2">From Game Account:</span>
                <span>{`${workflow.fromGameAccount?.kioskAccount?.platform?.name} [${workflow.fromGameAccount?.username}]` || "-"}</span>
              </div>
            </div>
            
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <div className="d-flex">
                <span className="text-muted mr-2">Payment Type:</span>
                <span>{workflow.paymentType?.name || "-"}</span>
              </div>
            </div>
            <div className="col-6">
              <div className="d-flex">
                <span className="text-muted mr-2">Player Bank:</span>
                <span>{workflow.playerBankAccount ? `${workflow.playerBankAccount.bank?.name} - ${workflow.playerBankAccount.accountName} / ${workflow.playerBankAccount.accountNo}` : "-"}</span>
              </div>
            </div>
          </div>
          <div className="row mb-3">
            {workflow.completedAt && (
              <div className="col-6">
                <div className="d-flex">
                  <span className="text-muted mr-2">Completed At:</span>
                  <span>{moment(workflow.completedAt).format("YYYY-MM-DD HH:mm:ss")}</span>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {/* {workflow.errorMessage && (
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="alert alert-danger py-2 mb-0 flex-grow-1 mr-2" role="alert">
                <small><i className="bx bx-error-circle mr-1"></i>{workflow.errorMessage}</small>
              </div>
              <Button
                color="warning"
                size="sm"
                onClick={() => onSupersede && onSupersede(workflow)}
                title="Supersede this failed withdrawal with a new one"
              >
                <i className="bx bx-revision mr-1"></i>
                Supersede
              </Button>
            </div>
          )} */}

          {/* Transactions Section */}
          {workflow.transactions?.edges?.length > 0 && (
            <div className="mt-3 pt-3 border-top transactions-page">
              <h6 
                className="mb-2 font-size-14 cursor-pointer d-flex align-items-center justify-content-between" 
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ cursor: 'pointer' }}
              >
                <span>
                  <i className="bx bx-transfer mr-1"></i>
                  Transactions ({workflow.transactions.edges.length})
                </span>
                <i className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'} font-size-18`}></i>
              </h6>
              {isExpanded && (
                <div className="table-responsive" style={workflow.transactions?.edges?.length === 1 ? {overflow: "visible"} : {overflow: "scroll"}}>
                  <Table className="table mb-0">
                    <thead>
                      <tr className="font-size-12">
                        <th>Serial No</th>
                        <th>Created At</th>
                        <th>Type</th>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Image</th>
                        <th>Amount</th>
                        <th>Movement</th>
                        <th>Kiosk Remark</th>
                        <th>Updated At</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {_.sortBy(workflow.transactions.edges, ['node.stepNumber']).map((transaction, txnIndex) => (
                        <TransactionItem
                          key={txnIndex}
                          refetch={handleRefresh}
                          transaction={transaction}
                          onDone={handleRefresh}
                          isFromWorkflow
                          allowSupersede={transaction.node.transactionType === "Withdrawal" && (transaction.node.transactionStatus !== "Completed" && transaction.node.transactionStatus !== "Pending") && _.isEmpty(transaction.node.supersededById)}
                          onSupersede={() => onSupersede(workflow)}
                          onManageInWorkbench={() => {
                            history.push({
                              pathname: "/cashier-workbench",
                              target: "_blank",
                              state: {
                                user: {
                                  node: transaction.node.player,
                                },
                              },
                            });
                          }}
                        />
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* Updated At Footer */}
          <div className="mt-3 pt-2 border-top text-muted font-size-12">
            <i className="bx bx-time-five mr-1"></i>
            Updated: {moment(workflow.updatedAt).format("YYYY-MM-DD HH:mm:ss")}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

function WorkflowWithdrawal(props) {
  const { t } = props;
  const first = 50;

  const [search, setSearch] = React.useState("");
  const [searchValue, setSearchValue] = React.useState("");
  const [isSearch, setIsSearch] = React.useState(false);
  const [afterCursor, setAfterCursor] = React.useState(null);
  const [pageInfo, setPageInfo] = React.useState(false);
  const [isFetchMore, setIsFetchMore] = React.useState(false);
  const [orderBy, setOrderBy] = React.useState("createdAt");
  const [orderDirection, setOrderDirection] = React.useState("DESC");
  const [isCreateModal, setIsCreateModal] = React.useState(false);
  const [supersedeWorkflow, setSupersedeWorkflow] = React.useState(null);
  
  // Form state
  const [selectedPlayer, setSelectedPlayer] = React.useState([]);
  const [isValidPlayer, setIsValidPlayer] = React.useState(true);
  const [selectedPaymentType, setSelectedPaymentType] = React.useState("");
  const [selectedPaymentTypeName, setSelectedPaymentTypeName] = React.useState("");
  const [isValidPaymentType, setIsValidPaymentType] = React.useState(true);
  const [quantity, setQuantity] = React.useState("");
  
  // Bank account state (for Local Bank Transfer)
  const [selectedPlayerBankAccount, setSelectedPlayerBankAccount] = React.useState("");
  const [isValidPlayerBankAccount, setIsValidPlayerBankAccount] = React.useState(true);
  const [selectedCompanyBankAccount, setSelectedCompanyBankAccount] = React.useState("");
  const [isValidCompanyBankAccount, setIsValidCompanyBankAccount] = React.useState(true);
  
  // Receipt upload state
  const [selectedFile, setSelectedFile] = React.useState(null);

  const baseFilter = {
    workflowType: { eq: "DirectWithdrawFromGame" },
  };

  const Filter = () => {
    if (_.isEmpty(search)) {
      return { ...baseFilter };
    } else {
      return {
        ...baseFilter,
        or: [
          { serialNo: { iLike: `%${search}%` } },
          { player: { username: { iLike: `%${search}%` } } },
        ],
      };
    }
  };

  const {
    data: workflowData,
    fetchMore,
    loading,
    refetch,
  } = useQuery(GET_WORKFLOWS, {
    variables: {
      paging: { first: first },
      filter: Filter(),
      sorting: [{ field: orderBy, direction: orderDirection }],
    },
    fetchPolicy: "network-only",
  });

  const { data: paymentTypesData } = useQuery(GET_PAYMENT_TYPES);

  // Fetch player bank accounts for selected player
  const { data: playerBankAccountsData } = useQuery(GET_BANK_ACCOUNTS, {
    variables: {
      paging: { first: 50 },
      filter: {
        playerId: { eq: selectedPlayer[0]?.node?.id },
        isActive: { is: true },
      },
    },
    skip: selectedPlayer.length === 0,
    fetchPolicy: "network-only",
  });

  // Fetch company bank accounts that can withdraw
  const { data: companyBankAccountsData } = useQuery(GET_BANK_ACCOUNTS, {
    variables: {
      paging: { first: 50 },
      filter: {
        accountHolderType: { eq: "Company" },
        isActive: { is: true },
        canWithdraw: { is: true },
      },
    },
    fetchPolicy: "network-only",
  });

  const [getMainCreditWallet, { data: mainCreditWalletData }] = useMutation(FIND_OR_CREATE_MAIN_CREDIT_WALLET, {
        variables: {
            userId: selectedPlayer[0]?.node?.id
        }
    })
  

  const [createWithdrawal, { loading: createLoading }] = useMutation(
    CREATE_ONE_WITHDRAWAL
  );

  const [linkTransactionToWorkflow] = useMutation(LINK_TRANSACTION_TO_WORKFLOW);
  

  React.useEffect(() => {
    if (workflowData) {
      setAfterCursor(workflowData.workflows.pageInfo.endCursor);
      setPageInfo(workflowData.workflows.pageInfo);
    }
  }, [workflowData]);

  React.useEffect(() => {
    if (selectedPlayer.length === 1) {
      getMainCreditWallet();
    }
  }, [selectedPlayer]);

  React.useEffect(() => {
    if (isFetchMore) {
      fetchMore({
        variables: {
          paging: { first: first, after: afterCursor },
          filter: Filter(),
          sorting: [{ field: orderBy, direction: orderDirection }],
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.workflows.edges;
          const pageInfo = fetchMoreResult.workflows.pageInfo;
          setIsFetchMore(false);
          setAfterCursor(pageInfo.endCursor);

          return newEdges.length
            ? {
                workflows: {
                  __typename: previousResult.workflows.__typename,
                  edges: _.uniqBy(
                    [...previousResult.workflows.edges, ...newEdges],
                    "node.id"
                  ),
                  pageInfo,
                },
              }
            : previousResult;
        },
      });
    }
  }, [isFetchMore]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSave = useRef(
    _.debounce((nextValue) => setSearch(nextValue), 300)
  ).current;

  const handleCreateWithdrawal = async () => {
    // Validate required fields
    let isValid = true;

    if (selectedPlayer.length === 0) {
      setIsValidPlayer(false);
      isValid = false;
    }

    if (!selectedPaymentType) {
      setIsValidPaymentType(false);
      isValid = false;
    }

    // Validate bank accounts for Local Bank Transfer
    if (selectedPaymentTypeName === "Local Bank Transfer") {
      if (!selectedPlayerBankAccount) {
        setIsValidPlayerBankAccount(false);
        isValid = false;
      }
      if (!selectedCompanyBankAccount) {
        setIsValidCompanyBankAccount(false);
        isValid = false;
      }
    }

    if (!quantity || parseFloat(quantity) <= 0) {
      isValid = false;
    }

    if (!isValid) {
      ToastHelper.toggleToast({
        toastType: "error",
        message: "Please fill in all required fields",
        title: "",
        onClick: function () {},
      });
      return;
    }

    try {
      // Upload receipt if file selected
      let receiptUrl = null;
      if (selectedFile) {
        const imageData = await Compress(selectedFile);
        const result = await CloudinaryHelper.upload(
          imageData,
          `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload?upload_preset=rjhrtxfi`
        );
        receiptUrl = result.data.url;
      }

      const input = {
        playerId: selectedPlayer[0].node.id,
        amount: parseFloat(quantity.toString().replaceAll(",", "")),
        paymentTypeId: selectedPaymentType,
        fromWalletId: mainCreditWalletData?.findOrCreateMainCreditWallet?.id,
        ...(selectedPaymentTypeName === "Local Bank Transfer" && {
          playerBankAccountId: selectedPlayerBankAccount,
          companyBankAccountId: selectedCompanyBankAccount,
        }),
        ...(receiptUrl && { receiptUrl }),
      };

      const withdrawalResult = await createWithdrawal({
        variables: {
          input: {
            withdrawal: {...input},
          },
        },
      });

      // Link the new transaction to the superseded workflow
      if (supersedeWorkflow && withdrawalResult?.data?.createOneWithdrawal?.id) {
        await linkTransactionToWorkflow({
          variables: {
            input: {
              transactionId: withdrawalResult.data.createOneWithdrawal.id,
              workflowId: supersedeWorkflow.id,
              stepNumber: 4, // Withdrawal step in workflow
            },
          },
        });
      }

      ToastHelper.toggleToast({
        message: "Withdrawal workflow created successfully!",
        title: "",
        onClick: function () {},
      });
      resetModal();
      refetch();
    } catch (e) {
      ToastHelper.toggleToast({
        toastType: "error",
        message: e.message || e,
        title: "",
        onClick: function () {},
      });
    }
  };

  const resetModal = () => {
    setIsCreateModal(false);
    setSupersedeWorkflow(null);
    setSelectedPlayer([]);
    setSelectedPaymentType("");
    setSelectedPaymentTypeName("");
    setQuantity("");
    setIsValidPlayer(true);
    setIsValidPaymentType(true);
    setSelectedPlayerBankAccount("");
    setIsValidPlayerBankAccount(true);
    setSelectedCompanyBankAccount("");
    setIsValidCompanyBankAccount(true);
    setSelectedFile(null);
  };

  const handleSupersede = (workflow) => {
    // Store the workflow for supersede
    setSupersedeWorkflow(workflow);
    // Pre-populate the modal with the player from the failed workflow
    if (workflow.player) {
      setSelectedPlayer([{
        node: workflow.player
      }]);
      setIsValidPlayer(true);
    }
    // Set quantity from workflow
    if (workflow.quantity) {
      setQuantity(workflow.quantity.toString());
    }
    // Set default payment type to first available option
    const localBankTransfer = paymentTypesData?.paymentTypes?.edges.find(
      (pt) => pt.node.name === "Local Bank Transfer"
    );
    if (localBankTransfer) {
      setSelectedPaymentType(localBankTransfer.node.id);
      setSelectedPaymentTypeName(localBankTransfer.node.name);
      setIsValidPaymentType(true);
    }
    setIsCreateModal(true);
  };

  // Compress image before upload
  const Compress = (file) => {
    const fileType = file["type"];
    const validImageTypes = ["image/jpeg", "image/png"];
    if (!validImageTypes.includes(fileType)) {
      return file;
    }

    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 1,
        success: resolve,
        error: reject,
      });
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Workflow" breadcrumbItem="Withdrawal" />
          <div className="search-bar-container d-flex align-items-center">
            <div className="app-search d-none d-lg-block flex-grow-1 mr-2">
              <div className="position-relative">
                <input
                  value={searchValue}
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    debouncedSave(e.target.value);
                  }}
                />
                <span className="bx bx-search-alt"></span>
              </div>
            </div>
            <div className="dropdown d-inline-block d-lg-none flex-grow-1">
              <button
                type="button"
                className="btn header-item noti-icon waves-effect"
                onClick={() => setIsSearch(!isSearch)}
              >
                <i className="mdi mdi-magnify"></i>
              </button>
              <div
                className={
                  isSearch
                    ? "dropdown-menu dropdown-menu-lg dropdown-menu-right p-0 show"
                    : "dropdown-menu dropdown-menu-lg dropdown-menu-right p-0"
                }
              >
                <form className="p-3">
                  <div className="form-group m-0">
                    <div className="input-group">
                      <input
                        value={searchValue}
                        type="text"
                        className="form-control"
                        placeholder="Search..."
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          debouncedSave(e.target.value);
                        }}
                      />
                      <div className="input-group-append">
                        <button className="btn btn-primary" type="submit">
                          <i className="mdi mdi-magnify"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            {/* Create Withdrawal button hidden - form preserved below
            <Button
              color="primary"
              className="btn-sm flex-shrink-0"
              onClick={() => setIsCreateModal(true)}
            >
              <i className="bx bx-plus mr-1"></i>
              Create Withdrawal
            </Button>
            */}
          </div>
          {workflowData && (
            <InfiniteScroll
              dataLength={workflowData.workflows.edges.length}
              next={() => setIsFetchMore(true)}
              hasMore={pageInfo.hasNextPage}
              loader={
                <div className="text-center my-3">
                  <Link to="#" className="text-success">
                    <i className="bx bx-hourglass bx-spin mr-2"></i> Load more
                  </Link>
                </div>
              }
              endMessage={
                <div className="text-center my-3">
                  <div>Yay! You have seen it all</div>
                </div>
              }
            >
              <Row>
                {workflowData.workflows.edges.map((workflow, index) => (
                  <WorkflowItem key={workflow.node.id} workflow={workflow.node} onSupersede={handleSupersede} />
                ))}
              </Row>
            </InfiniteScroll>
          )}
          <div className="load-more-container">
            {loading && (
              <Col xs="12">
                <div className="text-center my-3">
                  <Link to="#" className="text-success">
                    <i className="bx bx-hourglass bx-spin mr-2"></i> Load more
                  </Link>
                </div>
              </Col>
            )}
          </div>
        </Container>
      </div>

      {/* Create Withdrawal Modal */}
      <Modal
        isOpen={isCreateModal}
        toggle={resetModal}
        size="lg"
        autoFocus={true}
        centered={true}
        tabIndex="-1"
      >
        <div className="modal-content new-account-page">
          <ModalHeader toggle={resetModal}>
            {supersedeWorkflow ? t("Supersede Withdrawal") : t("Create Withdrawal Workflow")}
          </ModalHeader>
          <ModalBody>
            <div className="twitter-bs-wizard">
              <AvForm
                className="needs-validation"
                onValidSubmit={handleCreateWithdrawal}
                onInvalidSubmit={() => {
                  if (selectedPlayer.length === 0) {
                    setIsValidPlayer(false);
                  }
                  if (selectedPaymentType.length === 0) {
                    setIsValidPaymentType(false);
                  }
                }}
              >
                <Row>
                  <Col lg="12">
                    <FormGroup className={`${!isValidPlayer ? "text-danger" : ""}`}>
                      <Label>{t("Player")} *</Label>
                      <AsyncTypeahead
                        id="player-search"
                        labelKey={(option) =>
                          `${option.node.serialNo}/${option.node.username}/${option.node.phoneNo || "-"}`
                        }
                        minLength={2}
                        placeholder={t("Search Player")}
                        useCache={false}
                        selected={selectedPlayer}
                        renderMenuItemChildren={(option) => (
                          <Fragment>
                            <span>{`${option.node.serialNo}/${option.node.username}/${option.node.phoneNo || "-"}`}</span>
                          </Fragment>
                        )}
                        isInvalid={!isValidPlayer}
                        onChange={(val) => {
                          setIsValidPlayer(val.length > 0);
                          setSelectedPlayer(val);
                        }}
                        onBlur={() => {
                          if (isValidPlayer && selectedPlayer.length === 0) {
                            setIsValidPlayer(false);
                          }
                        }}
                        disabled
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col lg="12">
                    <FormGroup className={`${!isValidPaymentType ? "text-danger" : ""}`}>
                      <Label>{t("Payment Type")} *</Label>
                      <select
                        className="custom-select"
                        value={selectedPaymentType}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          setSelectedPaymentType(selectedId);
                          setIsValidPaymentType(selectedId !== "");
                          // Find the selected payment type name
                          const selectedPt = paymentTypesData?.paymentTypes?.edges.find(
                            (pt) => pt.node.id === selectedId
                          );
                          setSelectedPaymentTypeName(selectedPt?.node?.name || "");
                          // Reset bank account selections when payment type changes
                          setSelectedPlayerBankAccount("");
                          setIsValidPlayerBankAccount(true);
                          setSelectedCompanyBankAccount("");
                          setIsValidCompanyBankAccount(true);
                        }}
                      >
                        <option value="">{t("-- Select Payment Type --")}</option>
                        {paymentTypesData?.paymentTypes?.edges
                          .filter((pt) =>
                            ["Local Bank Transfer"].includes(
                              pt.node.name
                            )
                          )
                          .map((pt) => (
                            <option key={pt.node.id} value={pt.node.id}>
                              {pt.node.name}
                            </option>
                          ))}
                      </select>
                    </FormGroup>
                  </Col>
                </Row>
                {/* Show bank account fields for Local Bank Transfer */}
                {selectedPaymentTypeName === "Local Bank Transfer" && (
                  <>
                    <Row>
                      <Col lg="12">
                        <FormGroup className={`${!isValidCompanyBankAccount ? "text-danger" : ""}`}>
                          <Label>{t("Company Bank Account")} *</Label>
                          <select
                            className="custom-select"
                            value={selectedCompanyBankAccount}
                            onChange={(e) => {
                              setSelectedCompanyBankAccount(e.target.value);
                              setIsValidCompanyBankAccount(e.target.value !== "");
                            }}
                          >
                            <option value="">{t("-- Select Company Bank Account --")}</option>
                            {companyBankAccountsData?.bankAccounts?.edges.map((ba) => (
                              <option key={ba.node.id} value={ba.node.id}>
                                {`${ba.node.bank?.name} - ${ba.node.accountName} - ${ba.node.accountNo}`}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="12">
                        <FormGroup className={`${!isValidPlayerBankAccount ? "text-danger" : ""}`}>
                          <Label>{t("Player Bank Account")} *</Label>
                          <select
                            className="custom-select"
                            value={selectedPlayerBankAccount}
                            onChange={(e) => {
                              setSelectedPlayerBankAccount(e.target.value);
                              setIsValidPlayerBankAccount(e.target.value !== "");
                            }}
                            disabled={selectedPlayer.length === 0}
                          >
                            <option value="">
                              {selectedPlayer.length === 0
                                ? t("Select player first")
                                : t("-- Select Player Bank Account --")}
                            </option>
                            {playerBankAccountsData?.bankAccounts?.edges.map((ba) => (
                              <option key={ba.node.id} value={ba.node.id}>
                                {`${ba.node.bank?.name} - ${ba.node.accountName} - ${ba.node.accountNo}`}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                )}
                <Row>
                  <Col lg="12">
                    <AvField
                      value={quantity}
                      label={`${t("Amount")} *`}
                      name="quantity"
                      type="text"
                      placeholder={t("Amount")}
                      errorMessage={t("Enter Amount")}
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      tag={[Input, NumberFormat]}
                      disabled
                      validate={{
                        required: { value: true },
                        pattern: {
                          value: "^[0-9,.]+$",
                          errorMessage: t("Enter Only Numbers"),
                        },
                      }}
                    />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col lg="12">
                    <Label>{t("Receipt")} ({t("Optional")})</Label>
                    <DropZone
                      selectedFile={selectedFile}
                      onFileDrop={(file) => {
                        setSelectedFile(file);
                      }}
                    />
                  </Col>
                </Row>
                <div className="action-container mt-3">
                  <Button color="secondary" className="mr-2" onClick={resetModal}>
                    {t("Cancel")}
                  </Button>
                  <Button color="primary" type="submit" disabled={createLoading}>
                    {createLoading && <i className="fas fa-spinner fa-spin mr-2"></i>}
                    {t("Submit")}
                  </Button>
                </div>
              </AvForm>
            </div>
          </ModalBody>
        </div>
      </Modal>
    </React.Fragment>
  );
}

const mapStatetoProps = (state) => {
  const { layoutType } = state.Layout;
  return { layoutType };
};

export default connect(mapStatetoProps, { toggleRightSidebar })(
  withTranslation()(WorkflowWithdrawal)
);
